import { Component, OnInit, Input, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { RmsModule } from '@rms';
import { MatTreeModule } from '@angular/material/tree';
import { GroupManagementComponent } from './group-management/group-management.component'
import { InventoryDataService, PromptService, WebService, RmsMessageService, lastValueFrom, RmsPulldownService } from '@services';

@Component({
  selector: 'app-coffee-grouping',
  templateUrl: './coffee-grouping.component.html',
  styleUrl: './coffee-grouping.component.scss',
  imports: [RmsModule, MatTreeModule]
})
export class CoffeeGroupingComponent implements OnInit, OnChanges {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;
  Products: any[] = [];
  InventoryData: any = {
    Regions: [],
    Countries: [],
    Groups: [],
    SKUs: []
  };
  Groups: any[] = []
  GroupSearch: string = '';
  SelectedRegion: any = null;
  SelectedCountry: any = null;
  SkuSearch: string = '';
  selectedSkus = new Set<any>()
  dataSource: any[] = [];
  scrollingInterval: any = null;
  isDraggingGlobal = false;

  // right side lists
  filteredGroups: any[] = [];
  filteredUngrouped: any[] = [];
  filteredArchived: any[] = [];
  filteredCountries: any[] = [];
  constructor(private inventoryDataService: InventoryDataService, private prompt: PromptService, private web: WebService, private message: RmsMessageService, private pulldown: RmsPulldownService) { }

  async ngOnInit() {
    await this.loadData();
  }

  ngOnChanges() {
    if (this.Products?.length && this.InventoryData.Groups?.length) {
      this.buildTree();
    }
  }

  async loadData() {
    const data = await this.inventoryDataService.getData({
      keys: ['Regions', 'Countries', 'Groups', 'Products']
    });
    this.Products = data['Products'];
    this.InventoryData = { ...this.InventoryData, ...data };
    this.InventoryData.Countries = this.InventoryData.Countries.sort((a: any, b: any) => a.CountryName.localeCompare(b.CountryName));
    this.buildTree();
    this.applyFilters();
  }

  // ✅ BUILD TREE + INCLUDE CHILD SKU AGGREGATION
  buildTree() {
    const buildNode = (group: any): any => {
      const children = this.sortByName(
        this.InventoryData.Groups
          .filter((g: any) => g.ParentID === group.GroupID)
          .map(buildNode),
        'GroupName'
      );

      return {
        ...group,
        name: group.GroupName,
        children,
        skus: this.sortByName(
          this.getAllSkusForGroup(group.GroupID),
          'InventoryTypeName'
        )
      };
    };

    const roots = this.sortByName(
      this.InventoryData.Groups
        .filter((g: any) => !g.ParentID)
        .map(buildNode),
      'GroupName'
    );

    this.Groups = roots;
  }
  // ✅ RECURSIVE SKU COLLECTION
  getAllSkusForGroup(groupId: number): any[] {
    let result = [];

    const children = this.InventoryData.Groups
      .filter((g: any) => g.ParentID === groupId);

    const direct = this.Products
      .filter((s: any) => Number(s.CoffeeSKUGroupID) === Number(groupId));


    result.push(...direct);

    for (const child of children) {
      result.push(...this.getAllSkusForGroup(child.GroupID));
    }

    return result;
  }
  dragCounterMap = new Map<number, number>();

  onDragEnter(group: any) {
    const id = group.GroupID;
    const count = (this.dragCounterMap.get(id) || 0) + 1;

    this.dragCounterMap.set(id, count);
    group.expanded = true;
  }

  onDragLeave(group: any) {
    const id = group.GroupID;
    let count = (this.dragCounterMap.get(id) || 0) - 1;

    if (count <= 0) {
      this.dragCounterMap.set(id, 0);
      group.expanded = false;
    } else {
      this.dragCounterMap.set(id, count);
    }
  }

  onDragReset(group: any) {
    const id = group.GroupID;
    this.dragCounterMap.set(id, 0);
    group.expanded = false;
  }
  // ✅ FILTERING (SEARCH + REGION + COUNTRY)
  applyFilters() {
    console.log("Applying filters...");

    const skuSearch = this.SkuSearch?.toLowerCase() || '';
    const groupSearch = this.GroupSearch?.toLowerCase() || '';

    // ✅ SKU match
    const matchesSku = (sku: any) => {
      return (
        sku.InventoryTypeName?.toLowerCase().includes(skuSearch) ||
        sku.InventoryTypeID?.toString().includes(skuSearch)
      );
    };

    // ✅ Group match
    const matchesGroup = (group: any) => {
      return (
        group.GroupName?.toLowerCase().includes(groupSearch) ||
        group.GroupID?.toString().includes(groupSearch)
      );
    };

    // ✅ Check if group OR any descendants contain matching SKU
    const groupHasMatch = (group: any): boolean => {
      if (group.skus?.some(matchesSku)) return true;

      if (group.children?.some((child: any) => groupHasMatch(child))) {
        return true;
      }

      return false;
    };

    // ✅ MAIN GROUP PROCESSING (no filtering out 🔥)
    this.filteredGroups = this.Groups
      .filter((g: any) =>
        this.SelectedCountry ? g.CountryID === this.SelectedCountry : true
    )
      .filter((group: any) =>
      !groupSearch || matchesGroup(group))
      .map((group: any) => {
        const hasSkuMatch = groupHasMatch(group);
        const hasGroupMatch = matchesGroup(group);

        return {
          ...group,
          expanded: (skuSearch && hasSkuMatch) || (groupSearch && hasGroupMatch),

          // ✅ optional: only show matching SKUs when searching
          skus: skuSearch
            ? (group.skus || []).filter(matchesSku)
            : group.skus
        };
      });

    // ✅ Ungrouped
    this.filteredUngrouped = this.Products
      .filter((s: any) => !s.CoffeeSKUGroupID && !s.Archived)
      .filter(matchesSku);

    // ✅ Archived
    this.filteredArchived = this.Products
      .filter((s: any) => s.Archived)
      .filter(matchesSku);

    // ✅ Countries
    this.filteredCountries = this.InventoryData.Countries
      .filter((c: any) =>
        this.SelectedRegion ? c.RegionID === this.SelectedRegion : true
      );
  }
  clearFilters() {
    this.SelectedRegion = null;
    this.SelectedCountry = null;
    this.SkuSearch = '';
    this.applyFilters();
  }

  expandAll() {
    // hook into your treeControl if needed
  }

  // ✅ ACTIONS
  assignSku(sku: any) {
    // assign to selected group logic
  }

  removeSkuFromGroup(group: any, sku: any) {
    // remove logic
  }


  toggleSelection(sku: any) {
    if (this.selectedSkus.has(sku)) {
      this.selectedSkus.delete(sku);
    } else {
      this.selectedSkus.add(sku);
    }
  }

  getDragPayload(sku: any) {
    if (this.selectedSkus.size > 0) {
      return Array.from(this.selectedSkus);
    }
    return [sku];
  }

  startAutoScroll(direction: 'up' | 'down') {
    if (this.scrollingInterval) return;

    this.scrollingInterval = setInterval(() => {
      const container = this.scrollContainer.nativeElement;
      container.scrollTop += direction === 'down' ? 10 : -10;
    }, 16); // ~60fps
  }

  stopAutoScroll() {
    clearInterval(this.scrollingInterval);
    this.scrollingInterval = null;
  }
  autoScroll(event: DragEvent) {
    const container = this.scrollContainer.nativeElement;
    const rect = container.getBoundingClientRect();

    const buffer = 60;
    const mouseY = event.clientY;

    if (mouseY < rect.top + buffer) {
      this.startAutoScroll('up');
    } else if (mouseY > rect.bottom - buffer) {
      this.startAutoScroll('down');
    } else {
      this.stopAutoScroll();
    }
  }

  onDragStart(event: DragEvent, sku: any) {
    this.isDraggingGlobal = true;
    console.log(this.isDraggingGlobal);
    const selected = this.selectedSkus.size
      ? Array.from(this.selectedSkus)
      : [sku];

    const preview = document.createElement('div');
    preview.classList.add('drag-preview');

    // 👇 grab Angular-scoping attribute
    const ngAttr = this.getNgAttr(event.target as HTMLElement);

    if (ngAttr) preview.setAttribute(ngAttr, '');

    selected.slice(0, 4).forEach((s, i) => {
      const item = document.createElement('div');
      item.classList.add('drag-preview-item');

      // 👇 apply it to children too
      if (ngAttr) item.setAttribute(ngAttr, '');

      item.style.setProperty('--i', String(i));
      item.innerText = s.InventoryTypeName;

      preview.appendChild(item);
    });

    document.body.appendChild(preview);

    event.dataTransfer?.setDragImage(preview, 20, 20);

    setTimeout(() => preview.remove(), 0);
  }
  private getNgAttr(el: HTMLElement): string | null {
    return el.getAttributeNames().find(a => a.startsWith('_ngcontent-')) ?? null;
  }
  async onDropToUngrouped(skus: any) {
    const { closed$ } = this.prompt.open({
      promptText: 'Are you sure you want to remove the SKUs from their groups?',
      buttons: [{ label: 'Yes', value: true, buttonClass: 'btn-bg-green' }]
    });

    closed$.subscribe(async (value) => {
      if (!value) return;
      for (const sku of skus) {
        await this.UpdateSkuGrouping(sku, null);
      }
    })
  }

  async onDropToGroup(skus: any, group: any) {

    const skuList = Array.isArray(skus) ? skus : [skus];

    const { closed$ } = this.prompt.open({
      promptText: 'Are you sure you want to update the SKU grouping?',
      buttons: [{ label: 'Yes', value: true, buttonClass: 'btn-bg-green' }]
    });

    closed$.subscribe(async (value) => {
      if (!value) return;
      console.log(skuList, group);
      for (const sku of skuList) {
        await this.UpdateSkuGrouping(sku, group.GroupID);
      }
    });

    this.selectedSkus.clear();
  }
  onDragEnd() {
    this.stopAutoScroll();
    this.isDraggingGlobal = false;
  }
  async UpdateSkuGrouping(sku: any, newGroupId: number | null, single: boolean = false) {
    if (single) {
      const { closed$ } = this.prompt.open({
        promptText: 'Are you sure you want to update the SKU grouping?',
        buttons: [{ label: 'Yes', value: true, buttonClass: 'btn-bg-green' }]
      });

      closed$.subscribe(async (value) => {
        if (!value) return;
        try {
          await lastValueFrom(this.web.getWebCall('inventory', '/coffee/products/update-group', { body: { InventoryTypeID: sku.InventoryTypeID, GroupID: newGroupId } }));
          await this.loadData();
          this.message.success('SKU grouping updated successfully');
        }
        catch (error) {
          this.message.error('Failed to update SKU grouping: ' + error);
        }
      })
    }
    else {
      try {
        await lastValueFrom(this.web.getWebCall('inventory', '/coffee/products/update-group', { body: { InventoryTypeID: sku.InventoryTypeID, GroupID: newGroupId } }));
        await this.loadData();
        this.message.success('SKU grouping updated successfully');
      }
      catch (error) {
        this.message.error('Failed to update SKU grouping: ' + error);
      }
    }
    
  }
  async onOpenGroupManagement(Group?: any) {
    if (Group) {
      Group.Relation = Group.IsParent ? 'Parent' : 'Child';
    }
    const reff = this.pulldown.open(GroupManagementComponent, {
      title: 'Create New Group',
      top: '0',
      width: 'clamp(520px, 55vw, 980px)',
      maxWidth: '100vw',
      data: Group ? { ...Group } : null
    })

    reff.afterClosed().subscribe(async result => {
      if (!result) return;
      if (result.cancelled) return;

      result.IsParent = result.Relation === 'Parent' ? true : false;
      result.Active = true;
      try {
        await lastValueFrom(this.web.getWebCall('inventory', '/coffee/products/merge-group', { body: result }));
        await this.loadData();
        this.message.success(`Group ${Group ? 'updated' : 'created'} successfully`);
      }
      catch (error) {
        console.error(error);
      }
      
    })
  }
  async onArchive(sku: any, Archive: boolean) {
    sku.Archived = Archive;
    const { closed$ } = this.prompt.open({
      promptText: `Are you sure you want to ${Archive ? 'archive' : 'unarchive'} this SKU?`,
      buttons: [{ label: 'Yes', value: true, buttonClass: 'btn-bg-green' }]
    })

    closed$.subscribe(async (value) => {
      if (!value) return;

      try {
        await lastValueFrom(this.web.getWebCall('inventory', 'coffee/products/set-archive', { params: [{ name: 'InventoryTypeID', value: sku.InventoryTypeID }, { name: 'Archived', value: Archive }] }));
        this.message.success(`SKU ${Archive ? 'archived' : 'unarchived'} successfully`);
        await this.loadData();
      }
      catch (error) {
        console.error(error);
      }
    })
  }
  async onDeleteGroup(Group: any) {
    const { closed$ } = this.prompt.open({
      promptText: `Are you sure you want to delete the group "${Group.GroupName}"? This action cannot be undone. All SKUs in this group will be ungrouped afterwords.`,
      buttons: [{ label: 'Yes', value: true, buttonClass: 'btn-bg-green' }]
    });

    closed$.subscribe(async (value) => {
      if (!value) return;

      try {
        await lastValueFrom(this.web.getWebCall('inventory', 'coffee/products/delete-group', { params: [{ name: 'GroupID', value: Group.GroupID }] }));
        this.message.success(`Group "${Group.GroupName}" deleted successfully`);
        await this.loadData();
      }
      catch (error) {
        console.error(error);
      }
    });
  }

  private sortByName<T>(arr: T[], key: keyof T): T[] {
    return [...arr].sort((a: any, b: any) =>
      (a[key] ?? '').localeCompare(b[key] ?? '', undefined, {
        sensitivity: 'base' // ✅ case-insensitive
      })
    );
  }

}
